import { TextField } from '@blueprinthq/joy'
import { Box, Button, Image, Link, Text, VStack } from '@chakra-ui/react'
import { Loading } from '@components'
import { trackEvent } from '@lib/clinician-tracking'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTelehealthControllerV1GetLookupClinician } from '~/public-api'
import { usePatientDailyManager } from './context/patient-daily-manager.context'

type ClientWelcomeParams = {
  clinicianDisplayId: string
}

export const ClientWelcome = () => {
  const [fullName, setFullName] = useState('')
  const [showClinicianNotFound, setShowClinicianNotFound] = useState(false)

  const { clinicianDisplayId } = useParams<ClientWelcomeParams>()

  const { setParticipantFullName } = usePatientDailyManager()

  const { data: clinicianInfo, isLoading: isClinicianInfoLoading } =
    useTelehealthControllerV1GetLookupClinician(
      {
        identifier: clinicianDisplayId
      },
      {
        query: {
          onError: () => {
            setShowClinicianNotFound(true)
          },
          enabled: !!clinicianDisplayId,
          retry: false
        }
      }
    )

  useEffect(() => {
    const savedName = localStorage.getItem('telehealth_fullname')

    if (savedName) {
      setFullName(savedName)
    }
  }, [])

  const handleSubmitFullName = async () => {
    localStorage.setItem('telehealth_fullname', fullName.trim())
    setParticipantFullName(fullName.trim())
    trackEvent('event:telehealth-session.participant-submitted-name', {
      clinician_display_id: clinicianDisplayId,
      full_name: fullName.trim()
    })
  }

  if (isClinicianInfoLoading) {
    return <Loading />
  }

  if (showClinicianNotFound) {
    return (
      <VStack spacing={4} w="100%">
        <Text textAlign="center" fontSize="24px">
          Sorry we can't find this room.
        </Text>
        <Image
          src="https://res.cloudinary.com/hellojoy/image/upload/v1748468819/images/clinician-room-not-found.png"
          alt="Room not found"
          w="100%"
          h="100%"
        />
        <Text textAlign="center" fontSize="16px" w="374px">
          Please double check the URL or contact your clinician for their latest
          room link.
        </Text>
      </VStack>
    )
  }

  return (
    <VStack spacing={4} w="100%">
      <Box textAlign="center">
        <Text fontSize="24px">Join Telehealth session</Text>
        <Text fontSize="24px" color="primary">
          with {clinicianInfo?.firstName} {clinicianInfo?.lastName}
        </Text>
      </Box>
      <Text alignSelf="flex-start" fontSize="16px" textAlign="left">
        Please check in to let {clinicianInfo?.firstName} know you're here.
      </Text>
      <form
        style={{ width: '100%' }}
        onSubmit={e => {
          e.preventDefault()
          if (fullName) {
            handleSubmitFullName()
          }
        }}
      >
        <VStack spacing={4} w="100%">
          <TextField
            label="Full name"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            bg="white"
            type="text"
          />
          <Button
            type="submit"
            isFullWidth
            size="lg"
            isDisabled={!fullName}
            bg={fullName ? 'primary' : '#F4F4F4'}
            color={fullName ? 'white' : '#A1A1A1'}
            data-click-text="Check in"
          >
            Check in
          </Button>
        </VStack>
      </form>
      <Text fontSize="10px" textAlign="center">
        By checking in, you agree to the{' '}
        <Link
          color="primary"
          target="_blank"
          href="/client-terms-of-service"
          _focus={{ outline: 'none' }}
          data-click-text="Terms of Service"
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          color="primary"
          target="_blank"
          href="/privacy-policy"
          _focus={{ outline: 'none' }}
          data-click-text="Privacy Policy"
        >
          Privacy Policy
        </Link>
        .
      </Text>
    </VStack>
  )
}
